/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Environment } from "../models/Environment";
import type { EnvironmentConfigVar } from "../models/EnvironmentConfigVar";
import type { Project } from "../models/Project";
import type { ProjectRoute } from "../models/ProjectRoute";
import type { Resource } from "../models/Resource";
import type { Service } from "../models/Service";
import type { ServiceConfigVar } from "../models/ServiceConfigVar";
import { request as __request } from "../core/request";

export class ProjectsService {
  /**
   * @param page A page number within the paginated result set.
   * @returns any
   * @throws ApiError
   */
  public static async projectsList(
    page?: number
  ): Promise<{
    count: number;
    next?: string | null;
    previous?: string | null;
    results: Array<Project>;
  }> {
    const result = await __request({
      method: "GET",
      path: `/projects/`,
      query: {
        page: page,
      },
    });
    return result.body;
  }

  /**
   * @param data
   * @returns Project
   * @throws ApiError
   */
  public static async projectsCreate(data: Project): Promise<Project> {
    const result = await __request({
      method: "POST",
      path: `/projects/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param name
   * @returns Project
   * @throws ApiError
   */
  public static async projectsRead(name: string): Promise<Project> {
    const result = await __request({
      method: "GET",
      path: `/projects/${name}/`,
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param page A page number within the paginated result set.
   * @returns any
   * @throws ApiError
   */
  public static async projectsEnvironmentsList(
    projectName: string,
    page?: number
  ): Promise<{
    count: number;
    next?: string | null;
    previous?: string | null;
    results: Array<Environment>;
  }> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/environments/`,
      query: {
        page: page,
      },
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param data
   * @returns Environment
   * @throws ApiError
   */
  public static async projectsEnvironmentsCreate(
    projectName: string,
    data: Environment
  ): Promise<Environment> {
    const result = await __request({
      method: "POST",
      path: `/projects/${projectName}/environments/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param environmentPk
   * @param projectName
   * @param page A page number within the paginated result set.
   * @returns any
   * @throws ApiError
   */
  public static async projectsEnvironmentsConfigsList(
    environmentPk: string,
    projectName: string,
    page?: number
  ): Promise<{
    count: number;
    next?: string | null;
    previous?: string | null;
    results: Array<EnvironmentConfigVar>;
  }> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/environments/${environmentPk}/configs/`,
      query: {
        page: page,
      },
    });
    return result.body;
  }

  /**
   * @param environmentPk
   * @param projectName
   * @param data
   * @returns EnvironmentConfigVar
   * @throws ApiError
   */
  public static async projectsEnvironmentsConfigsCreate(
    environmentPk: string,
    projectName: string,
    data: EnvironmentConfigVar
  ): Promise<EnvironmentConfigVar> {
    const result = await __request({
      method: "POST",
      path: `/projects/${projectName}/environments/${environmentPk}/configs/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param environmentPk
   * @param id
   * @param projectName
   * @returns EnvironmentConfigVar
   * @throws ApiError
   */
  public static async projectsEnvironmentsConfigsRead(
    environmentPk: string,
    id: string,
    projectName: string
  ): Promise<EnvironmentConfigVar> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/environments/${environmentPk}/configs/${id}/`,
    });
    return result.body;
  }

  /**
   * @param environmentPk
   * @param id
   * @param projectName
   * @param data
   * @returns EnvironmentConfigVar
   * @throws ApiError
   */
  public static async projectsEnvironmentsConfigsUpdate(
    environmentPk: string,
    id: string,
    projectName: string,
    data: EnvironmentConfigVar
  ): Promise<EnvironmentConfigVar> {
    const result = await __request({
      method: "PUT",
      path: `/projects/${projectName}/environments/${environmentPk}/configs/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param environmentPk
   * @param id
   * @param projectName
   * @param data
   * @returns EnvironmentConfigVar
   * @throws ApiError
   */
  public static async projectsEnvironmentsConfigsPartialUpdate(
    environmentPk: string,
    id: string,
    projectName: string,
    data: EnvironmentConfigVar
  ): Promise<EnvironmentConfigVar> {
    const result = await __request({
      method: "PATCH",
      path: `/projects/${projectName}/environments/${environmentPk}/configs/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param environmentPk
   * @param id
   * @param projectName
   * @returns any
   * @throws ApiError
   */
  public static async projectsEnvironmentsConfigsDelete(
    environmentPk: string,
    id: string,
    projectName: string
  ): Promise<any> {
    const result = await __request({
      method: "DELETE",
      path: `/projects/${projectName}/environments/${environmentPk}/configs/${id}/`,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @returns Environment
   * @throws ApiError
   */
  public static async projectsEnvironmentsRead(
    id: string,
    projectName: string
  ): Promise<Environment> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/environments/${id}/`,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param data
   * @returns Environment
   * @throws ApiError
   */
  public static async projectsEnvironmentsUpdate(
    id: string,
    projectName: string,
    data: Environment
  ): Promise<Environment> {
    const result = await __request({
      method: "PUT",
      path: `/projects/${projectName}/environments/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param data
   * @returns Environment
   * @throws ApiError
   */
  public static async projectsEnvironmentsPartialUpdate(
    id: string,
    projectName: string,
    data: Environment
  ): Promise<Environment> {
    const result = await __request({
      method: "PATCH",
      path: `/projects/${projectName}/environments/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @returns any
   * @throws ApiError
   */
  public static async projectsEnvironmentsDelete(
    id: string,
    projectName: string
  ): Promise<any> {
    const result = await __request({
      method: "DELETE",
      path: `/projects/${projectName}/environments/${id}/`,
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param page A page number within the paginated result set.
   * @returns any
   * @throws ApiError
   */
  public static async projectsServicesList(
    projectName: string,
    page?: number
  ): Promise<{
    count: number;
    next?: string | null;
    previous?: string | null;
    results: Array<Service>;
  }> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/services/`,
      query: {
        page: page,
      },
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param data
   * @returns Service
   * @throws ApiError
   */
  public static async projectsServicesCreate(
    projectName: string,
    data: Service
  ): Promise<Service> {
    const result = await __request({
      method: "POST",
      path: `/projects/${projectName}/services/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @returns Service
   * @throws ApiError
   */
  public static async projectsServicesRead(
    id: string,
    projectName: string
  ): Promise<Service> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/services/${id}/`,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param data
   * @returns Service
   * @throws ApiError
   */
  public static async projectsServicesUpdate(
    id: string,
    projectName: string,
    data: Service
  ): Promise<Service> {
    const result = await __request({
      method: "PUT",
      path: `/projects/${projectName}/services/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param data
   * @returns Service
   * @throws ApiError
   */
  public static async projectsServicesPartialUpdate(
    id: string,
    projectName: string,
    data: Service
  ): Promise<Service> {
    const result = await __request({
      method: "PATCH",
      path: `/projects/${projectName}/services/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @returns any
   * @throws ApiError
   */
  public static async projectsServicesDelete(
    id: string,
    projectName: string
  ): Promise<any> {
    const result = await __request({
      method: "DELETE",
      path: `/projects/${projectName}/services/${id}/`,
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param page A page number within the paginated result set.
   * @returns any
   * @throws ApiError
   */
  public static async projectsRoutesList(
    projectName: string,
    page?: number
  ): Promise<{
    count: number;
    next?: string | null;
    previous?: string | null;
    results: Array<ProjectRoute>;
  }> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/routes/`,
      query: {
        page: page,
      },
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param data
   * @returns Service
   * @throws ApiError
   */
  public static async projectsRoutesCreate(
    projectName: string,
    data: ProjectRoute
  ): Promise<ProjectRoute> {
    const result = await __request({
      method: "POST",
      path: `/projects/${projectName}/routes/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @returns Service
   * @throws ApiError
   */
  public static async projectsRoutesRead(
    id: string,
    projectName: string
  ): Promise<ProjectRoute> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/routes/${id}/`,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param data
   * @returns Service
   * @throws ApiError
   */
  public static async projectsRoutesUpdate(
    id: string,
    projectName: string,
    data: ProjectRoute
  ): Promise<ProjectRoute> {
    const result = await __request({
      method: "PUT",
      path: `/projects/${projectName}/routes/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param data
   * @returns ProjectRoute
   * @throws ApiError
   */
  public static async projectsRoutesPartialUpdate(
    id: string,
    projectName: string,
    data: ProjectRoute
  ): Promise<ProjectRoute> {
    const result = await __request({
      method: "PATCH",
      path: `/projects/${projectName}/services/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @returns any
   * @throws ApiError
   */
  public static async projectsRoutesDelete(
    id: string,
    projectName: string
  ): Promise<any> {
    const result = await __request({
      method: "DELETE",
      path: `/projects/${projectName}/routes/${id}/`,
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param servicePk
   * @param page A page number within the paginated result set.
   * @returns any
   * @throws ApiError
   */
  public static async projectsServicesConfigsList(
    projectName: string,
    servicePk: string,
    page?: number
  ): Promise<{
    count: number;
    next?: string | null;
    previous?: string | null;
    results: Array<ServiceConfigVar>;
  }> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/services/${servicePk}/configs/`,
      query: {
        page: page,
      },
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param servicePk
   * @param data
   * @returns ServiceConfigVar
   * @throws ApiError
   */
  public static async projectsServicesConfigsCreate(
    projectName: string,
    servicePk: string,
    data: ServiceConfigVar
  ): Promise<ServiceConfigVar> {
    const result = await __request({
      method: "POST",
      path: `/projects/${projectName}/services/${servicePk}/configs/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param servicePk
   * @returns ServiceConfigVar
   * @throws ApiError
   */
  public static async projectsServicesConfigsRead(
    id: string,
    projectName: string,
    servicePk: string
  ): Promise<ServiceConfigVar> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/services/${servicePk}/configs/${id}/`,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param servicePk
   * @param data
   * @returns ServiceConfigVar
   * @throws ApiError
   */
  public static async projectsServicesConfigsUpdate(
    id: string,
    projectName: string,
    servicePk: string,
    data: ServiceConfigVar
  ): Promise<ServiceConfigVar> {
    const result = await __request({
      method: "PUT",
      path: `/projects/${projectName}/services/${servicePk}/configs/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param servicePk
   * @param data
   * @returns ServiceConfigVar
   * @throws ApiError
   */
  public static async projectsServicesConfigsPartialUpdate(
    id: string,
    projectName: string,
    servicePk: string,
    data: ServiceConfigVar
  ): Promise<ServiceConfigVar> {
    const result = await __request({
      method: "PATCH",
      path: `/projects/${projectName}/services/${servicePk}/configs/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id
   * @param projectName
   * @param servicePk
   * @returns any
   * @throws ApiError
   */
  public static async projectsServicesConfigsDelete(
    id: string,
    projectName: string,
    servicePk: string
  ): Promise<any> {
    const result = await __request({
      method: "DELETE",
      path: `/projects/${projectName}/services/${servicePk}/configs/${id}/`,
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param servicePk
   * @param page A page number within the paginated result set.
   * @returns any
   * @throws ApiError
   */
  public static async projectsServicesResourcesList(
    projectName: string,
    servicePk: string,
    page?: number
  ): Promise<{
    count: number;
    next?: string | null;
    previous?: string | null;
    results: Array<Resource>;
  }> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/services/${servicePk}/resources/`,
      query: {
        page: page,
      },
    });
    return result.body;
  }

  /**
   * @param projectName
   * @param servicePk
   * @param data
   * @returns Resource
   * @throws ApiError
   */
  public static async projectsServicesResourcesCreate(
    projectName: string,
    servicePk: string,
    data: Resource
  ): Promise<Resource> {
    const result = await __request({
      method: "POST",
      path: `/projects/${projectName}/services/${servicePk}/resources/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id A unique integer value identifying this resource.
   * @param projectName
   * @param servicePk
   * @returns Resource
   * @throws ApiError
   */
  public static async projectsServicesResourcesRead(
    id: number,
    projectName: string,
    servicePk: string
  ): Promise<Resource> {
    const result = await __request({
      method: "GET",
      path: `/projects/${projectName}/services/${servicePk}/resources/${id}/`,
    });
    return result.body;
  }

  /**
   * @param id A unique integer value identifying this resource.
   * @param projectName
   * @param servicePk
   * @param data
   * @returns Resource
   * @throws ApiError
   */
  public static async projectsServicesResourcesUpdate(
    id: number,
    projectName: string,
    servicePk: string,
    data: Resource
  ): Promise<Resource> {
    const result = await __request({
      method: "PUT",
      path: `/projects/${projectName}/services/${servicePk}/resources/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id A unique integer value identifying this resource.
   * @param projectName
   * @param servicePk
   * @param data
   * @returns Resource
   * @throws ApiError
   */
  public static async projectsServicesResourcesPartialUpdate(
    id: number,
    projectName: string,
    servicePk: string,
    data: Resource
  ): Promise<Resource> {
    const result = await __request({
      method: "PATCH",
      path: `/projects/${projectName}/services/${servicePk}/resources/${id}/`,
      body: data,
    });
    return result.body;
  }

  /**
   * @param id A unique integer value identifying this resource.
   * @param projectName
   * @param servicePk
   * @returns any
   * @throws ApiError
   */
  public static async projectsServicesResourcesDelete(
    id: number,
    projectName: string,
    servicePk: string
  ): Promise<any> {
    const result = await __request({
      method: "DELETE",
      path: `/projects/${projectName}/services/${servicePk}/resources/${id}/`,
    });
    return result.body;
  }
}
